import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { serviceData } from '../../../data/DCGJProcess';
import { serviceDataArabic } from '../../../data/DCGJProcessArabic';
import { faqData } from '../../../data/faq.data';
import { RoutesPath } from '../../constants/routes-path';

type SearchItemType = 'service' | 'page' | 'faq';

interface SearchItem {
  id: string;
  labelEn: string;
  labelAr: string;
  route: string;
  type: SearchItemType;
  categoryKey?: string;
  keywords?: string[];
}

@Component({
  selector: 'app-global-search',
  imports: [CommonModule, TranslateModule],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss'
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() isOpen = false;
  @Input() title = '';
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() onBtnClick = new EventEmitter<any>();
  searchText = '';
  results: SearchItem[] = [];
  private allItems: SearchItem[] = [];
  private translationsEn: Record<string, any> = {};
  private translationsAr: Record<string, any> = {};
  private currentLang = 'en';

  constructor(
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.currentLang = this.getLang();
    forkJoin({
      en: this.translate.getTranslation('en'),
      ar: this.translate.getTranslation('ar')
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ en, ar }) => {
        this.translationsEn = en || {};
        this.translationsAr = ar || {};
        this.allItems = [
          ...this.buildServiceItems(),
          ...this.buildStaticItems(),
          ...this.buildFaqItems()
        ];
        this.applySearch();
      });

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentLang = this.getLang();
        this.applySearch();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleSearch(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.searchText = target?.value ?? '';
    this.applySearch();
  }

  clearSearch(): void {
    this.searchText = '';
    this.results = [];
  }

  navigateTo(item: SearchItem): void {
    if (!item?.route) {
      return;
    }
    this.router.navigateByUrl(item.route);
    this.isOpenChange.emit(false);
    this.clearSearch();
  }

  displayLabel(item: SearchItem): string {
    if (this.currentLang === 'ar') {
      return item.labelAr || item.labelEn;
    }
    return item.labelEn || item.labelAr;
  }

  displayCategory(item: SearchItem): string {
    if (!item.categoryKey) {
      return '';
    }
    const translations = this.currentLang === 'ar' ? this.translationsAr : this.translationsEn;
    return this.getTranslationValue(translations, item.categoryKey);
  }

  private applySearch(): void {
    const term = this.normalize(this.searchText);
    if (!term) {
      this.results = [];
      return;
    }
    this.results = this.allItems.filter((item) => this.matches(item, term));
  }

  private matches(item: SearchItem, term: string): boolean {
    const candidates = [
      item.labelEn,
      item.labelAr,
      ...(item.keywords || [])
    ];
    return candidates.some((text) => this.normalize(text).includes(term));
  }

  private normalize(value: string): string {
    return (value || '').toLowerCase().trim();
  }

  private getLang(): string {
    return (this.translate.currentLang || localStorage.getItem('selectedLang') || 'en').toLowerCase();
  }

  private buildServiceItems(): SearchItem[] {
    const arabicById = new Map<string, any>();
    for (const item of serviceDataArabic.Items || []) {
      if (item?.id) {
        arabicById.set(item.id, item);
      }
    }

    const items: SearchItem[] = [];
    const seen = new Set<string>();
    for (const item of serviceData.Items || []) {
      // if (item?.Visible === false) {
      //   continue;
      // }
      const id = item?.id;
      if (!id || seen.has(id)) {
        continue;
      }
      const labelEn = (item?.serviceName || this.extractServiceName(item?.SRType) || '').trim();
      if (!labelEn) {
        continue;
      }
      const arabicItem = arabicById.get(id);
      const labelAr = (arabicItem?.serviceName || '').toString().trim() || labelEn;
      items.push({
        id,
        labelEn,
        labelAr,
        route: `/${RoutesPath.SERVICE_DETAIL}/${id}`,
        type: 'service',
        categoryKey: 'SERVICES.TITLE'
      });
      seen.add(id);
    }
    return items;
  }

  private buildStaticItems(): SearchItem[] {
    const pages: Array<{
      id: string;
      route: string;
      labelKey: string;
      type: SearchItemType;
      keywords?: string[];
    }> = [
      {
        id: 'faq',
        route: `/${RoutesPath.FAQ}`,
        labelKey: 'HEADER.FAQ',
        type: 'faq'
      },
      {
        id: 'service-manuals',
        route: `/${RoutesPath.SERVICE_MANUALS}`,
        labelKey: 'MANUALS.TITLE',
        type: 'page',
        keywords: ['resource', 'resources', 'manual', 'manuals', 'service manual', 'service manuals']
      },
      {
        id: 'dashboard',
        route: `/${RoutesPath.DASHBOARD}`,
        labelKey: 'MENU_DASHBOARD',
        type: 'page'
      },
      {
        id: 'account-management',
        route: `/${RoutesPath.ACCOUNT_MANAGEMENT}`,
        labelKey: 'MENU_ACCOUNT',
        type: 'page',
        keywords: ['manage account', 'account management']
      },
      {
        id: 'all-services',
        route: `/${RoutesPath.SERVICES_LIST}`,
        labelKey: 'MENU_SERVICES',
        type: 'page',
        keywords: ['all services', 'services list']
      },
      {
        id: 'applications',
        route: `/${RoutesPath.APPLICATIONS}`,
        labelKey: 'MENU_APPLICATIONS',
        type: 'page'
      },
      {
        id: 'documents',
        route: `/${RoutesPath.DOCUMENTS}`,
        labelKey: 'MENU_DOCUMENTS',
        type: 'page'
      },
      {
        id: 'venues',
        route: `/${RoutesPath.VENUES}`,
        labelKey: 'MENU_VENUE',
        type: 'page'
      },
      {
        id: 'events',
        route: `/${RoutesPath.VENUES}`,
        labelKey: 'MENU_EVENTS',
        type: 'page',
        keywords: ['event booking', 'event bookings', 'venue booking', 'venue bookings']
      },
      {
        id: 'payments',
        route: `/${RoutesPath.PAYMENTS}`,
        labelKey: 'MENU_PAYMENTS',
        type: 'page'
      },
      {
        id: 'statements',
        route: `/${RoutesPath.STATEMENTS}`,
        labelKey: 'MENU_STATEMENT_OF_ACCOUNT',
        type: 'page',
        keywords: ['statement of accounts', 'statements']
      },
      {
        id: 'profile',
        route: `/${RoutesPath.PROFILE}`,
        labelKey: 'PROFILE.BTN.INFO',
        type: 'page',
        keywords: ['profile', 'account', 'user profile']
      },
      {
        id: 'settings',
        route: `/${RoutesPath.PROFILE}`,
        labelKey: 'NOTIFICATIONS.SETTINGS',
        type: 'page',
        keywords: ['settings', 'account settings']
      },
      {
        id: 'change-password',
        route: `/${RoutesPath.PROFILE}`,
        labelKey: 'PROFILE.BTN.PASSWORD',
        type: 'page',
        keywords: ['change password', 'reset password', 'password']
      },
      {
        id: 'notifications',
        route: `/${RoutesPath.NOTIFICATIONS}`,
        labelKey: 'NOTIFICATIONS.TITLE',
        type: 'page',
        keywords: ['alerts', 'updates']
      },
      {
        id: 'mediation',
        route: `/${RoutesPath.MEDIATION}`,
        labelKey: 'MENU_MEDIATION',
        type: 'page',
        keywords: ['case', 'cases']
      },
      {
        id: 'directory',
        route: `/${RoutesPath.DIRECTORY}`,
        labelKey: 'HEADER.DIRECTORY',
        type: 'page'
      },
      {
        id: 'business-groups-councils',
        route: `/${RoutesPath.BUSINESS_GROUPS_COUNCILS}`,
        labelKey: 'MENU_BUSINESS_GROUP',
        type: 'page',
        keywords: ['business group', 'business groups', 'council', 'councils']
      },
      {
        id: 'contact-us',
        route: `/${RoutesPath.CONTACT_US}`,
        labelKey: 'HEADER.CONTACT_US',
        type: 'page'
      }
    ];

    return pages.map((item) => {
      const labelEn = this.getTranslationValue(this.translationsEn, item.labelKey) || item.labelKey;
      const labelAr = this.getTranslationValue(this.translationsAr, item.labelKey) || labelEn;
      return {
        id: item.id,
        labelEn,
        labelAr,
        route: item.route,
        type: item.type,
        keywords: item.keywords
      };
    });
  }

  private buildFaqItems(): SearchItem[] {
    const items = (faqData?.faqs || []).map((faq, index) => {
      const labelEn = (faq?.question_en || '').toString().trim();
      const labelAr = (faq?.question_ar || '').toString().trim();
      const answerEn = (faq?.answer_en || faq?.aKeanswer_en || '').toString().trim();
      const answerAr = (faq?.answer_ar || '').toString().trim();
      if (!labelEn && !labelAr) {
        return null;
      }
      return {
        id: `faq-${index}`,
        labelEn: labelEn || labelAr,
        labelAr: labelAr || labelEn,
        route: `/${RoutesPath.FAQ}`,
        type: 'faq',
        categoryKey: 'HEADER.FAQ',
        keywords: [answerEn, answerAr].filter(Boolean)
      } as SearchItem;
    });

    return items.filter((item): item is SearchItem => !!item);
  }

  private getTranslationValue(map: Record<string, any>, key: string): string {
    if (!map || !key) {
      return '';
    }
    const parts = key.split('.');
    let current: any = map;
    for (const part of parts) {
      if (!current || typeof current !== 'object' || !(part in current)) {
        return '';
      }
      current = current[part];
    }
    return typeof current === 'string' ? current : '';
  }

  private extractServiceName(srType: string): string {
    const parts = String(srType || '').split('||');
    if (parts.length > 1) {
      return parts[1].trim();
    }
    return (parts[0] || '').trim();
  }
}
